import { useQuery } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";

export interface POExtractionJob {
  id: string;
  purchase_order_id: string;
  file_id: string;
  status: "pending" | "extracting" | "normalizing" | "calculating" | "completed" | "failed";
  extraction_result: Record<string, unknown> | null;
  ai_normalization_result: Record<string, unknown> | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export type ResolvedExtractionJob = POExtractionJob & {
  /** True when the latest in-progress job exceeded the stale threshold. */
  isStale?: boolean;
};

const IN_PROGRESS_STATUSES = new Set([
  "pending",
  "extracting",
  "normalizing",
  "calculating",
]);

/** Jobs stuck in-progress longer than this are treated as failed in the UI. */
export const STALE_EXTRACTION_MS = 10 * 60 * 1000;

export function getJobStartedAt(job: POExtractionJob): number {
  const raw = job.started_at ?? job.created_at;
  return new Date(raw).getTime();
}

export function isJobInProgress(status: POExtractionJob["status"]): boolean {
  return IN_PROGRESS_STATUSES.has(status);
}

export function isJobStale(job: POExtractionJob): boolean {
  if (!isJobInProgress(job.status)) return false;
  return Date.now() - getJobStartedAt(job) > STALE_EXTRACTION_MS;
}

/**
 * Pick the job the UI should reflect. Avoids masking a completed run when a
 * newer duplicate/stuck in-progress job was spawned by auto-retry.
 */
export function resolveActiveExtractionJob(
  jobs: POExtractionJob[],
): ResolvedExtractionJob | null {
  if (jobs.length === 0) return null;

  const sorted = [...jobs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const latest = sorted[0];

  if (!isJobInProgress(latest.status)) {
    return latest;
  }

  if (isJobStale(latest)) {
    const completed = sorted.find((job) => job.status === "completed");
    if (completed) return completed;

    return {
      ...latest,
      isStale: true,
    };
  }

  return latest;
}

export function usePOExtractionJob(fileId: string | null) {
  return useQuery({
    queryKey: ["po-extraction-job", fileId],
    queryFn: async () => {
      if (!fileId) return null;

      const { data, error } = await supabase
        .from("po_extraction_jobs")
        .select("*")
        .eq("file_id", fileId)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      if (!data?.length) return null;

      return resolveActiveExtractionJob(data as POExtractionJob[]);
    },
    enabled: !!fileId,
    refetchInterval: (data) => {
      const job = data.state.data;
      if (!job) return false;
      if (job.isStale) return false;
      if (isJobInProgress(job.status)) {
        return 2000;
      }
      return false;
    },
  });
}

export function usePOExtractionJobs(purchaseOrderId: string | null) {
  return useQuery({
    queryKey: ["po-extraction-jobs", purchaseOrderId],
    queryFn: async () => {
      if (!purchaseOrderId) return [];

      const { data, error } = await supabase
        .from("po_extraction_jobs")
        .select("*")
        .eq("purchase_order_id", purchaseOrderId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as POExtractionJob[];
    },
    enabled: !!purchaseOrderId,
  });
}

export function isEmptyExtraction(job: POExtractionJob | null): boolean {
  if (!job || job.status !== "completed") return false;

  const result = job.extraction_result as {
    success?: boolean;
    tables?: unknown[];
    text?: string
  } | null;

  if (!result?.success) return false;

  const hasNoTables = !result.tables || result.tables.length === 0;
  const hasNoText = !result.text || result.text.trim().length < 50;

  return hasNoTables && hasNoText;
}

export async function triggerPOExtraction(
  fileId: string,
  purchaseOrderId: string,
  organisationId: string
): Promise<{ success: boolean; jobId?: string; error?: string }> {
  const getErrorStatus = (err: unknown): number | undefined => {
    if (!err || typeof err !== "object") return undefined;
    const status = (err as { status?: unknown }).status;
    if (typeof status === "number") return status;
    const contextStatus = (err as { context?: { status?: unknown } }).context?.status;
    if (typeof contextStatus === "number") return contextStatus;
    return undefined;
  };

  try {
    const { data, error } = await invokeFunction(supabase, "process-po-extraction-v2", {
      body: {
        fileId,
        purchaseOrderId,
        organisationId,
      },
    });

    if (error) {
      console.error("Extraction trigger error:", error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return {
        success: false,
        jobId: data?.jobId,
        error: data?.error ?? "Failed to start extraction",
      };
    }

    return {
      success: true,
      jobId: data.jobId,
    };
  } catch (err) {
    const status = getErrorStatus(err);
    if (status === 401 || status === 403) {
      return {
        success: false,
        error: status === 401
          ? "Unauthorized — please sign in again."
          : "You do not have permission to extract this purchase order.",
      };
    }

    // Network/gateway errors: job may still have been created server-side
    console.warn("Extraction trigger failed (job may still be running):", err);
    return { success: true, error: "Request failed but extraction may be in progress" };
  }
}
